/**
 * Retry utility with exponential backoff
 */
export interface RetryOptions {
  maxRetries?: number;
  baseDelay?: number;
  maxDelay?: number;
}

const defaultOptions: Required<RetryOptions> = {
  maxRetries: 3,
  baseDelay: 500,
  maxDelay: 5000,
};

function isRetryableError(error: unknown): boolean {
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    return (
      message.includes('network') ||
      message.includes('timeout') ||
      message.includes('fetch') ||
      message.includes('connection') ||
      message.includes('econnrefused') ||
      message.includes('500') ||
      message.includes('502') ||
      message.includes('503') ||
      message.includes('504')
    );
  }
  return false;
}

async function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Wrapper for Supabase queries with retry logic and exponential backoff
 */
export async function supabaseQueryWithRetry<T>(
  queryFn: () => PromiseLike<{ data: T | null; error: { message: string } | null }>
): Promise<{ data: T | null; error: { message: string } | null }> {
  const opts = defaultOptions;
  let lastError: { message: string } | null = null;

  for (let attempt = 0; attempt <= opts.maxRetries; attempt++) {
    try {
      const result = await queryFn();
      
      // If no error, return success
      if (!result.error) {
        return result;
      }

      // Check if error is retryable
      if (!isRetryableError(new Error(result.error.message))) {
        // Non-retryable error, return immediately
        return result;
      }

      lastError = result.error;

      // If we've exhausted retries, return the error
      if (attempt === opts.maxRetries) {
        console.error(`All ${opts.maxRetries} retry attempts failed:`, result.error);
        return result;
      }

      // Calculate delay with exponential backoff + jitter
      const exponentialDelay = opts.baseDelay * Math.pow(2, attempt);
      const jitter = Math.random() * 0.3 * exponentialDelay;
      const waitTime = Math.min(exponentialDelay + jitter, opts.maxDelay);

      console.log(`Retry attempt ${attempt + 1}/${opts.maxRetries} after ${Math.round(waitTime)}ms`);
      
      await delay(waitTime);
    } catch (err) {
      // Unexpected error during query execution
      const errorMessage = err instanceof Error ? err.message : String(err);
      
      if (!isRetryableError(err) || attempt === opts.maxRetries) {
        console.error(`Query failed after ${attempt + 1} attempts:`, errorMessage);
        return { 
          data: null, 
          error: { message: errorMessage }
        };
      }

      const exponentialDelay = opts.baseDelay * Math.pow(2, attempt);
      const jitter = Math.random() * 0.3 * exponentialDelay;
      const waitTime = Math.min(exponentialDelay + jitter, opts.maxDelay);

      console.log(`Retry attempt ${attempt + 1}/${opts.maxRetries} after ${Math.round(waitTime)}ms (exception)`);
      
      await delay(waitTime);
    }
  }

  return { 
    data: null, 
    error: lastError || { message: 'Unknown error after retries' }
  };
}
